// Phase 6.C — 시안 design/v3/SchedulePage.jsx:99-276 SectionCard port.
// 공통 / 오전 / 오후 / 전처리 / 퇴근 체크리스트 섹션 카드.
// 발송 버튼 onClick 은 toast (Phase 7 메일 인프라 도입 후 활성).

import { useState } from 'react';
import { Card, Icon, Button, useToast } from '../../components/primitives';
import { SCH_STAFF, SCH_LOW_STOCK, type ScheduleSection } from './data';

const CHAN: Record<
  string,
  { label: string; icon: string; color: string }
> = {
  sms: { label: '문자', icon: 'message', color: '#0891B2' },
  kakao: { label: '카톡', icon: 'message', color: '#FAB60E' },
  telegram: { label: '텔레', icon: 'send', color: '#229ED9' },
};

function ChannelChip({ ch, size = 11 }: { ch: string; size?: number }) {
  const c = CHAN[ch] ?? CHAN.sms;
  if (!c) return null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 7px',
        borderRadius: 999,
        fontSize: size,
        fontWeight: 600,
        background: c.color + '18',
        color: c.color,
      }}
    >
      <Icon name={c.icon} size={10} color={c.color} />
      {c.label}
    </span>
  );
}

interface SectionCardProps {
  section: ScheduleSection;
  /** 모바일 (tabBar 활성) 분기. 좁은 viewport 에서 섹션별 staff chips 숨김 + 컴팩트 padding. */
  isMobile?: boolean;
}

export function SectionCard({ section, isMobile = false }: SectionCardProps) {
  const [open, setOpen] = useState(true);
  const toast = useToast();
  const groupStaff = SCH_STAFF.filter((s) => s.group === section.id);

  const total = section.tasks.length;
  const done = section.tasks.filter((t) => t.answered).length;
  const pct = section.isResponse ? Math.round((done / Math.max(1, total)) * 100) : null;

  const onSend = () => {
    toast.info(
      `[${section.title}] 발송은 Phase 7 메일/SMS 인프라 도입 후 활성됩니다. (대상 ${groupStaff.length || '전체'} 명)`,
    );
  };

  return (
    <Card style={{ padding: 0, overflow: 'hidden' }}>
      {/* Header */}
      <div
        style={{
          padding: isMobile ? '14px 16px' : '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          borderBottom: open ? '1px solid var(--border-weak)' : 'none',
          background: section.accent + '08',
        }}
      >
        <span
          style={{
            width: 36,
            height: 36,
            borderRadius: 10,
            background: section.accent + '1c',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Icon name={section.icon} size={18} color={section.accent} />
        </span>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <div
              style={{
                fontSize: 16,
                fontWeight: 700,
                color: 'var(--fg-1)',
                letterSpacing: '-0.01em',
              }}
            >
              {section.title}
            </div>
            <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>{section.sub}</div>
          </div>
          {section.isResponse && pct != null && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
              <div
                style={{
                  flex: 1,
                  maxWidth: 160,
                  height: 6,
                  borderRadius: 999,
                  background: 'var(--bg-subtle)',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    width: pct + '%',
                    height: '100%',
                    background: section.accent,
                  }}
                />
              </div>
              <span
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  color: section.accent,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {done}/{total} 응답
              </span>
            </div>
          )}
        </div>

        {/* Staff chips (desktop/tablet only) */}
        {!isMobile && groupStaff.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {groupStaff.map((s) => (
              <span
                key={s.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  padding: '4px 9px',
                  borderRadius: 999,
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--border-weak)',
                  fontSize: 11,
                  color: 'var(--fg-2)',
                }}
              >
                <span
                  style={{
                    width: 5,
                    height: 5,
                    borderRadius: 999,
                    background: section.accent,
                  }}
                />
                {s.name}
                <ChannelChip ch={s.channel} />
              </span>
            ))}
          </div>
        )}

        <Button variant="primary" size="sm" onClick={onSend}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
            <Icon name="send" size={12} color="var(--primary-strong)" />
            발송
          </span>
        </Button>
        <button
          onClick={() => setOpen((o) => !o)}
          aria-label="toggle"
          style={{
            width: 30,
            height: 30,
            borderRadius: 8,
            background: 'transparent',
            border: '1px solid var(--border-weak)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 160ms',
          }}
        >
          <Icon name="chevron-right" size={14} color="var(--fg-3)" />
        </button>
      </div>

      {/* Body */}
      {open && (
        <div style={{ padding: isMobile ? '12px 16px 16px' : '16px 20px 18px' }}>
          {section.autoStock && (
            <div
              style={{
                padding: '12px 14px',
                marginBottom: 12,
                borderRadius: 10,
                background: 'var(--danger-bg)',
                border: '1px solid var(--danger)40',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                <Icon name="alert" size={14} color="var(--danger)" />
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--danger)' }}>
                  부족재고 자동연결 — 오늘 처리 필요 {SCH_LOW_STOCK.length}건
                </span>
                <span style={{ flex: 1 }} />
                <span style={{ fontSize: 11, color: 'var(--fg-3)' }}>전처리 조 출근시간 자동 +1h</span>
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                  gap: 6,
                }}
              >
                {SCH_LOW_STOCK.map((s) => (
                  <div
                    key={s.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 10px',
                      background: 'var(--bg-surface)',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                  >
                    <span
                      style={{ width: 5, height: 5, borderRadius: 999, background: 'var(--danger)' }}
                    />
                    <span style={{ color: 'var(--fg-1)', fontWeight: 500 }}>{s.name}</span>
                    <span style={{ flex: 1 }} />
                    <span style={{ color: 'var(--fg-3)', fontVariantNumeric: 'tabular-nums' }}>
                      {s.qty} → {s.need}
                    </span>
                    <span style={{ color: 'var(--fg-4)', fontSize: 11 }}>{s.store}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {section.tasks.map((t, i) => {
              const answered = !!t.answered;
              return (
                <div
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: section.isResponse
                      ? answered
                        ? '#F0FDF4'
                        : 'var(--bg-subtle)'
                      : 'var(--bg-subtle)',
                    border: section.isResponse && answered
                      ? '1px solid var(--success)'
                      : '1px solid transparent',
                  }}
                >
                  {section.isResponse ? (
                    <span
                      style={{
                        width: 18,
                        height: 18,
                        borderRadius: 4,
                        background: answered ? 'var(--success)' : 'transparent',
                        border: `1.5px solid ${answered ? 'var(--success)' : 'var(--border-base)'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {answered && (
                        <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>
                      )}
                    </span>
                  ) : (
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: 999,
                        background: section.accent,
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <span
                    style={{
                      flex: 1,
                      fontSize: 13,
                      color: section.isResponse && answered ? 'var(--fg-2)' : 'var(--fg-1)',
                      fontWeight: 500,
                      textDecoration: section.isResponse && answered ? 'line-through' : 'none',
                    }}
                  >
                    {t.t}
                  </span>
                  {!isMobile && (
                    <>
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--fg-3)',
                          fontVariantNumeric: 'tabular-nums',
                          minWidth: 90,
                          textAlign: 'right',
                        }}
                      >
                        {section.isResponse && answered ? t.by : t.time}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          color: 'var(--fg-3)',
                          padding: '2px 7px',
                          background: 'var(--bg-surface)',
                          border: '1px solid var(--border-weak)',
                          borderRadius: 999,
                          minWidth: 60,
                          textAlign: 'center',
                        }}
                      >
                        {t.who}
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {!section.isResponse && (
            <button
              type="button"
              style={{
                marginTop: 10,
                padding: '7px 12px',
                fontSize: 12,
                fontWeight: 600,
                color: 'var(--fg-3)',
                background: 'transparent',
                border: '1px dashed var(--border-base)',
                borderRadius: 8,
                cursor: 'pointer',
                width: '100%',
              }}
              onClick={() =>
                toast.info('항목 추가는 Phase 7 schedule 모델 도입 후 활성됩니다.')
              }
            >
              + 항목 추가
            </button>
          )}
        </div>
      )}
    </Card>
  );
}
